import React, { useState, useEffect } from "react";
import { getPaymentsByCustomerId } from "../../services/paymentService"; // API service
import toast from "react-hot-toast"; // For notifications

const PaymentHistory = ({ customerId }) => {
  const [payments, setPayments] = useState([]); // Store payment records
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const [paymentsPerPage] = useState(10); // Number of payments per page
  const [loading, setLoading] = useState(true); // Track loading state
  const [error, setError] = useState(null); // Track error state

  useEffect(() => {
    if (!customerId) {
      setError("Customer ID is required to fetch payment history.");
      setLoading(false);
      return;
    }

    // Fetch payments by customerId
    const fetchPayments = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getPaymentsByCustomerId(customerId); // Call API
        console.log("Payment history response:", response);

        if (!response.success) {
          // Handle API error response
          setError(response.message || "No payments found.");
          setPayments([]);
        } else {
          setPayments(response.data || []); // Update state with payment data
        }
      } catch (err) {
        console.error("Error fetching payments:", err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [customerId]);

  // Calculate payments for the current page
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = payments.slice(indexOfFirstPayment, indexOfLastPayment);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-500">Loading payment history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 font-medium">
        <p>{error}</p>
      </div>
    );
  }

  if (!payments.length) {
    return (
      <div className="text-center text-gray-500 font-medium">
        <p>No payment history found for this customer.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
        Payment History
      </h2>
      <div className="space-y-4">
        {currentPayments.map((payment) => (
          <div
            key={payment.id}
            className="flex flex-col bg-gray-50 rounded-lg p-4 shadow-md"
          >
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Date:</span>
              <span className="text-gray-800 font-semibold">
                {payment.payment_date}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">
                Amount Paid:
              </span>
              <span className="text-green-600 font-bold">
                ₹{payment.amount_paid}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">
                Payment Mode:
              </span>
              <span className="text-gray-800">{payment.payment_mode}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Notes:</span>
              <span className="text-gray-800">
                {payment.notes || "N/A"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-6">
        {Array.from(
          { length: Math.ceil(payments.length / paymentsPerPage) },
          (_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`mx-1 px-3 py-1 rounded-lg text-sm font-medium ${
                currentPage === index + 1
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              {index + 1}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
